import React, { useEffect, useState, useMemo } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { Section, Card, Badge, Alert, Button, Input, Label } from "../design-system";
import { carrierPreferredStatus, sortCarriersByPreferred } from "../lib/carrierHints";
import { PageHeader } from "../components/PageHeader";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
} from "../components/ui/dropdown-menu";
import { ChevronDown, ChevronRight, MoreHorizontal } from "lucide-react";

// Plain HTML table styled via .ds-table (matches DS look but supports children API)
const Table = ({ children, ...props }) => (
  <div className="ds-table-wrap"><table className="ds-table" {...props}>{children}</table></div>
);
import { useAuth } from "../auth/AuthProvider";

// ---------- Shared helpers ----------

function statusBadge(plan) {
  if (plan.status === "CANCELLED") return <Badge variant="danger">Annullato</Badge>;
  if (plan.status === "CONFIRMED") return <Badge variant="success">Confermato</Badge>;
  if (plan.status === "SENT_TO_CARRIER" && plan.has_unsent_changes)
    return <Badge variant="warning">Modificato dopo invio</Badge>;
  if (plan.status === "SENT_TO_CARRIER") return <Badge tone="info">Inviato al vettore</Badge>;
  return <Badge tone="neutral">Bozza</Badge>;
}

function fmtDate(iso) { return iso ? new Date(iso).toLocaleDateString("it-IT") : "—"; }
function fmtDateTime(iso) { return iso ? new Date(iso).toLocaleString("it-IT") : "—"; }
function fmtBool(v) { return v ? "Sì" : "No"; }

// ---------- Load Plans list ----------

export function LoadPlanList() {
  const { apiGet } = useAuth();
  const [plans, setPlans] = useState(null);
  const [carriers, setCarriers] = useState([]);
  const [tab, setTab] = useState("operativi");
  const [quick, setQuick] = useState("");
  const [filters, setFilters] = useState({ carrier_id: "", from: "", to: "" });
  const [search, setSearch] = useState("");
  const [searchResults, setSearchResults] = useState(null);
  const [searching, setSearching] = useState(false);

  const carrierName = (id) => carriers.find(c => c.id === id)?.business_name || "—";
  const iso = (d) => d.toISOString().slice(0, 10);

  const applyQuick = (which) => {
    setQuick(which);
    const t = new Date();
    if (which === "today") setFilters({ ...filters, from: iso(t), to: iso(t) });
    else if (which === "tomorrow") { const d = new Date(t); d.setDate(d.getDate() + 1);
      setFilters({ ...filters, from: iso(d), to: iso(d) }); }
    else if (which === "week") { const end = new Date(t); end.setDate(t.getDate() + 7);
      setFilters({ ...filters, from: iso(t), to: iso(end) }); }
    else setFilters({ ...filters, from: "", to: "" });
  };

  const statuses = useMemo(() => ({
    operativi: ["DRAFT", "SENT_TO_CARRIER"],
    bozze: ["DRAFT"], inviati: ["SENT_TO_CARRIER"],
    confermati: ["CONFIRMED"], archivio: null, // null = tutti
  }), []);

  const reload = async () => {
    const q = new URLSearchParams();
    if (filters.carrier_id) q.set("carrier_id", filters.carrier_id);
    if (filters.from) q.set("loading_date_from", filters.from);
    if (filters.to) q.set("loading_date_to", filters.to);
    const wanted = statuses[tab];
    if (wanted && wanted.length === 1) q.set("status", wanted[0]);
    const list = await apiGet(`/api/logistics/load-plans?${q.toString()}`);
    let all = Array.isArray(list) ? list : [];
    if (wanted && wanted.length > 1) all = all.filter(p => wanted.includes(p.status));
    // operativi tab: also exclude CANCELLED and stale defaults (already covered by DRAFT+SENT)
    if (tab === "operativi") all = all.filter(p => p.status !== "CANCELLED");
    // Sort ASC by loading_date for operational view
    if (["operativi","bozze","inviati"].includes(tab)) {
      all.sort((a, b) => a.loading_date.localeCompare(b.loading_date));
    }
    setPlans(all);
  };

  useEffect(() => { (async () => {
    try { setCarriers(await apiGet("/api/carriers") || []); } catch {}
    await reload();
  })(); /* eslint-disable-next-line */ }, []);
  useEffect(() => { reload(); /* eslint-disable-next-line */ }, [tab, JSON.stringify(filters)]);

  const runSearch = async (val) => {
    if (!val || val.trim().length < 2) { setSearchResults(null); return; }
    setSearching(true);
    try {
      const res = await apiGet(`/api/logistics/load-plans/search/orders?q=${encodeURIComponent(val.trim())}`);
      setSearchResults(res || []);
    } catch { setSearchResults([]); } finally { setSearching(false); }
  };

  const tabs = [
    { id: "operativi", label: "Operativi" },
    { id: "bozze", label: "Bozze" },
    { id: "inviati", label: "Da confermare" },
    { id: "confermati", label: "Confermati" },
    { id: "archivio", label: "Archivio / Tutti" },
  ];

  return (
    <Section data-testid="loadplan-list-page">
      <PageHeader
        breadcrumb={[
          { label: "Logistica", to: "/logistics" },
          { label: "Piano Carichi" },
        ]}
        title="Piano Carichi"
        subtitle="Pianificazione e conferma dei carichi ai vettori"
        actions={
          <Link to="/logistics/load-plans/new">
            <Button variant="primary" data-testid="lp-btn-create">+ Nuovo Piano</Button>
          </Link>
        }
      />

      {/* Search bar — sempre visibile, non sostituisce mai la lista */}
      <Card style={{ marginBottom: 12 }}>
        <Card.Body>
          <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
            <Input placeholder="Cerca ordine o cliente (numero, nome, codice)…"
                   value={search} data-testid="lp-global-search"
                   onChange={e => setSearch(e.target.value)}
                   onKeyDown={e => e.key === "Enter" && runSearch(search)}
                   style={{ flex: "1 1 260px" }} />
            <Button onClick={() => runSearch(search)} data-testid="lp-search-btn">Cerca</Button>
            {searchResults !== null && (
              <Button size="sm" data-testid="lp-search-close"
                      onClick={() => { setSearch(""); setSearchResults(null); }}>× Chiudi ricerca</Button>
            )}
          </div>
        </Card.Body>
      </Card>

      {/* Risultati ricerca — pannello sopra la lista, senza sostituirla */}
      {searchResults !== null && (
        <Card style={{ marginBottom: 12, borderLeft: "3px solid #0F4C81" }}
              data-testid="lp-search-panel">
          <Card.Header>
            <div style={{ display: "flex", justifyContent: "space-between",
                          alignItems: "center", gap: 8, flexWrap: "wrap" }}>
              <strong>Risultati ricerca ({searchResults.length})</strong>
              <span className="muted" style={{ fontSize: ".85rem" }}>
                Ricerca globale su tutti i tab · {" "}
                <a href="#" data-testid="lp-search-close-link"
                   onClick={(e) => { e.preventDefault(); setSearch(""); setSearchResults(null); }}
                   style={{ color: "#0F4C81" }}>Chiudi</a>
              </span>
            </div>
          </Card.Header>
          <Card.Body>
            {searching && <p className="muted">Ricerca…</p>}
            {!searching && searchResults.length === 0 && <Alert tone="info">Nessun risultato per "{search}"</Alert>}
            {!searching && searchResults.length > 0 && (
              <table className="ds-table" data-testid="lp-search-results">
                <thead><tr>
                  <th>Ordine</th><th>Cliente</th><th>Codice</th>
                  <th>Piano Carichi</th><th>Data carico</th><th>Vettore</th><th>Stato piano</th><th></th>
                </tr></thead>
                <tbody>
                  {searchResults.map(r => (
                    <tr key={`${r.order_id}-${r.load_plan_id || 'none'}`}
                        data-testid={`search-row-${r.order_number}`}>
                      <td><strong>{r.order_number}</strong></td>
                      <td>{r.customer_name}</td>
                      <td>{r.customer_code}</td>
                      <td>{r.load_plan_id ? `LP-${r.load_plan_loading_date}-${r.load_plan_id.slice(0,8)}`
                            : <span className="muted" data-testid={`search-noplan-${r.order_number}`}>
                                Non ancora assegnato a un Piano Carichi
                              </span>}</td>
                      <td>{r.load_plan_loading_date || "—"}</td>
                      <td>{r.carrier_id ? carrierName(r.carrier_id) : "—"}</td>
                      <td>{r.load_plan_status ? <Badge tone="info">{r.load_plan_status}</Badge> : "—"}</td>
                      <td>
                        {r.load_plan_id
                          ? <Link to={`/logistics/load-plans/${r.load_plan_id}`}>
                              <Button size="sm" data-testid={`search-open-plan-${r.order_number}`}>Apri Piano</Button>
                            </Link>
                          : <Link to={`/logistics/ship/${r.order_id}`}>
                              <Button size="sm" data-testid={`search-open-order-${r.order_number}`}>Apri ordine</Button>
                            </Link>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </Card.Body>
        </Card>
      )}

      {/* Lista Piani — tab + filtri sempre visibili anche durante la ricerca */}
      <Card>
        <Card.Header>
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 10 }}
               data-testid="lp-tabs-bar">
            {tabs.map(t => (
              <Button key={t.id} size="sm"
                      variant={tab === t.id ? "primary" : "default"}
                      data-testid={`lp-tab-${t.id}`}
                      onClick={() => setTab(t.id)}>{t.label}</Button>
            ))}
          </div>
          <div style={{ display: "flex", gap: 8, alignItems: "flex-end", flexWrap: "wrap" }}
               data-testid="lp-filters-bar">
            {[["today","Oggi"],["tomorrow","Domani"],["week","Questa settimana"],["","Tutti"]].map(([k,l]) => (
              <Button key={k || "all"} size="sm"
                      variant={quick === k ? "primary" : "default"}
                      data-testid={`lp-quick-${k || "all"}`}
                      onClick={() => applyQuick(k)}>{l}</Button>
            ))}
            <div>
              <Label>Vettore</Label>
              <select value={filters.carrier_id} data-testid="lp-filter-carrier"
                      onChange={e => setFilters({ ...filters, carrier_id: e.target.value })}
                      className="ds-input" style={{ minWidth: 180 }}>
                <option value="">Tutti</option>
                {carriers.map(c => <option key={c.id} value={c.id}>{c.business_name}</option>)}
              </select>
            </div>
            <div><Label>Da</Label><Input type="date" value={filters.from} data-testid="lp-filter-from"
                                          onChange={e => { setQuick(""); setFilters({ ...filters, from: e.target.value }); }} /></div>
            <div><Label>A</Label><Input type="date" value={filters.to} data-testid="lp-filter-to"
                                        onChange={e => { setQuick(""); setFilters({ ...filters, to: e.target.value }); }} /></div>
            <div style={{ marginLeft: "auto" }}>
              <Button size="sm" variant="secondary"
                      data-testid="lp-clear-filters"
                      onClick={() => { setQuick(""); setFilters({ carrier_id: "", from: "", to: "" }); }}>
                × Azzera filtri
              </Button>
            </div>
          </div>
        </Card.Header>
        <Card.Body>
          {!plans && <p className="muted">Caricamento…</p>}
          {plans && plans.length === 0 && <Alert tone="info" data-testid="lp-empty">Nessun piano in questo tab con i filtri correnti.</Alert>}
          {plans && plans.length > 0 && (
            <table className="ds-table" data-testid="loadplan-table">
              <thead><tr>
                <th>Data carico</th><th>Vettore</th><th>Stato</th>
                <th>N. ordini</th><th>Pallet</th><th>Kg</th><th></th>
              </tr></thead>
              <tbody>
                {plans.map(p => (
                  <tr key={p.id} data-testid={`lp-row-${p.id}`}>
                    <td><strong>{fmtDate(p.loading_date)}</strong></td>
                    <td>{carrierName(p.carrier_id)}</td>
                    <td>{statusBadge(p)}</td>
                    <td data-testid={`lp-orders-${p.id}`}>{p.orders_count ?? "—"}</td>
                    <td data-testid={`lp-pallets-${p.id}`}>{p.total_pallets ?? "—"}</td>
                    <td data-testid={`lp-kg-${p.id}`}>{p.total_kg ? Number(p.total_kg).toFixed(1) : "—"}</td>
                    <td>
                      <Link to={`/logistics/load-plans/${p.id}`}>
                        <Button size="sm" data-testid={`lp-open-${p.id}`}>Apri →</Button>
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Card.Body>
      </Card>
    </Section>
  );
}

// ---------- Create new Load Plan ----------

export function LoadPlanCreate() {
  const { apiGet, apiPost } = useAuth();
  const navigate = useNavigate();
  const [carriers, setCarriers] = useState([]);
  const [form, setForm] = useState({ carrier_id: "", loading_date: "", notes: "", delivery_method: "CARRIER" });
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => { (async () => setCarriers(await apiGet("/api/carriers") || []))(); }, []);

  const submit = async () => {
    if (busy) return;
    setErr(null);
    const isSender = form.delivery_method === "SENDER";
    if (!form.loading_date) { setErr("Data di carico obbligatoria"); return; }
    if (!isSender && !form.carrier_id) { setErr("Vettore obbligatorio per Piano con vettore"); return; }
    setBusy(true);
    try {
      const body = { ...form, carrier_id: isSender ? null : form.carrier_id };
      const r = await apiPost("/api/logistics/load-plans", body);
      navigate(`/logistics/load-plans/${r.id}`);
    } catch (e) {
      const d = e?.data?.detail; let msg = e?.message || String(e);
      if (typeof d === "object" && d?.code === "sender_plan_no_carrier_send") {
        msg = "Il Piano è gestito con consegna a cura del mittente e non richiede invio a un vettore.";
      } else if (typeof d === "object" && d?.message) msg = d.message;
      setErr(msg);
    } finally { setBusy(false); }
  };

  return (
    <Section title="Nuovo Piano Carichi" data-testid="loadplan-create-page">
      <Card><Card.Body>
        {err && <Alert variant="danger">{err}</Alert>}
        <div style={{ display: "grid", gap: 16, maxWidth: 520 }}>
          <div>
            <Label>Modalità consegna</Label>
            <select className="ds-input" value={form.delivery_method}
                    data-testid="lp-new-delivery-method"
                    onChange={(e) => setForm({ ...form, delivery_method: e.target.value,
                                                carrier_id: e.target.value === "SENDER" ? "" : form.carrier_id })}>
              <option value="CARRIER">Vettore</option>
              <option value="SENDER">Consegna a cura del mittente</option>
            </select>
          </div>
          {form.delivery_method === "CARRIER" && (
          <div>
            <Label>Vettore *</Label>
            <select className="ds-input" value={form.carrier_id} data-testid="lp-new-carrier"
                    onChange={(e) => setForm({ ...form, carrier_id: e.target.value })}>
              <option value="">— seleziona vettore —</option>
              {sortCarriersByPreferred(carriers, form.loading_date).map((c) => {
                const st = carrierPreferredStatus(c, form.loading_date);
                const marker = st === "match" ? " ✓ abituale" : "";
                return <option key={c.id} value={c.id}>{c.business_name}{marker}</option>;
              })}
            </select>
            {(() => {
              const sel = carriers.find(c => c.id === form.carrier_id);
              const st = carrierPreferredStatus(sel, form.loading_date);
              if (st === "match") return <div data-testid="lp-new-carrier-hint-match" style={{ fontSize: ".78rem", color: "#16a34a", marginTop: 4 }}>Giorno abituale per questo vettore</div>;
              if (st === "mismatch") return <div data-testid="lp-new-carrier-hint-mismatch" style={{ fontSize: ".78rem", color: "#d97706", marginTop: 4 }}>⚠ Giorno non abituale per questo vettore</div>;
              return null;
            })()}
          </div>
          )}
          <div>
            <Label>Data di carico *</Label>
            <Input type="date" value={form.loading_date} data-testid="lp-new-date"
                   onChange={(e) => setForm({ ...form, loading_date: e.target.value })} />
          </div>
          <div>
            <Label>Note</Label>
            <Input value={form.notes} data-testid="lp-new-notes"
                   onChange={(e) => setForm({ ...form, notes: e.target.value })} />
          </div>
          <div style={{ display: "flex", gap: 12 }}>
            <Button variant="primary" onClick={submit} loading={busy} disabled={busy}
                    data-testid="lp-new-submit">Crea Piano</Button>
            <Link to="/logistics/load-plans"><Button>Annulla</Button></Link>
          </div>
        </div>
      </Card.Body></Card>
    </Section>
  );
}

// ---------- Load Plan detail ----------

export function LoadPlanDetail() {
  const { id } = useParams();
  const { apiGet, apiPost, apiDelete, token } = useAuth();
  const navigate = useNavigate();
  const [plan, setPlan] = useState(null);
  const [snaps, setSnaps] = useState([]);
  const [carriers, setCarriers] = useState([]);
  const [picker, setPicker] = useState(false);
  const [moveOf, setMoveOf] = useState(null); // shipment being moved
  const [consuntivaOf, setConsuntivaOf] = useState(null); // shipment being consuntivata
  const [deliveredOf, setDeliveredOf] = useState(null);   // shipment being marked delivered
  const [notice, setNotice] = useState(null);
  const [error, setError] = useState(null);
  const [confirming, setConfirming] = useState(false);
  const [confirmErrors, setConfirmErrors] = useState(null);
  const [carrierOpen, setCarrierOpen] = useState(false); // Vettore assegnato accordion — chiuso di default
  // Mar 2026 — pending flag shared by mutation actions on this LoadPlan
  // (send-to-carrier / confirm / remove-shipment / cancel). Prevents double
  // click and lights up the spinner on the corresponding Button.
  const [mutating, setMutating] = useState(false);

  const reload = async () => {
    try {
      const p = await apiGet(`/api/logistics/load-plans/${id}`);
      setPlan(p);
      const s = await apiGet(`/api/logistics/load-plans/${id}/snapshots`);
      setSnaps(s || []);
    } catch (e) { setError(e.message); }
  };

  useEffect(() => {
    (async () => {
      setCarriers(await apiGet("/api/carriers") || []);
      await reload();
    })();
    // eslint-disable-next-line
  }, [id]);

  if (error) return <Section title="Piano Carichi"><Alert variant="danger">{error}</Alert></Section>;
  if (!plan) return <Section title="Piano Carichi"><Card><Card.Body>Caricamento…</Card.Body></Card></Section>;

  const carrier = carriers.find((c) => c.id === plan.carrier_id) || plan.carrier;
  const isMutable = plan.status === "DRAFT" || plan.status === "SENT_TO_CARRIER";
  const canConfirm = plan.status === "SENT_TO_CARRIER" || plan.status === "DRAFT";

  const doAction = async (fn, successMsg) => {
    if (mutating) return;
    setNotice(null); setError(null);
    setMutating(true);
    try { await fn(); setNotice(successMsg); await reload(); }
    catch (e) {
      const code = e?.data?.detail?.code || e?.message || String(e);
      const status = e?.status;
      const key = typeof code === "string" ? code : "";
      // 401 → sessione scaduta (token JWT expired, TTL 15 min).
      // FastAPI restituisce {"detail": "Invalid or expired token"}: qui lo
      // mostriamo human-readable invece del testo grezzo axios.
      if (status === 401 || key === "Invalid or expired token") {
        setError("Sessione scaduta. Effettua nuovamente il login e riprova.");
        return;
      }
      // 404 sul path plan_id: piano rimosso o id stale nella UI.
      if (status === 404 && key === "load_plan_not_found") {
        setError("Piano Carichi non trovato. Ricarica la pagina: potrebbe essere stato eliminato o l'id è cambiato.");
        return;
      }
      // 500 imprevisto: mai stringhe grezze axios all'operatore.
      if (status === 500 || status >= 500) {
        setError("Errore imprevisto sul server. Riprova; se il problema persiste contatta l'assistenza.");
        return;
      }
      const friendly = {
        carrier_email_missing:
          "Il vettore selezionato non ha un indirizzo email configurato. Inserisci l'email del vettore prima di prenotare il carico.",
        empty_plan:
          "Il Piano non contiene spedizioni. Aggiungi almeno una spedizione prima di procedere.",
        load_plan_status_not_sendable:
          "Il Piano non è in uno stato che consente l'invio al vettore.",
        load_plan_has_unsent_changes:
          "Ci sono modifiche non inviate al vettore: invia la revisione prima di confermare.",
        load_plan_not_confirmable:
          "Il Piano non è confermabile: verifica stato e revisioni.",
      }[key];
      setError(friendly || (typeof code === "string" ? code : JSON.stringify(code)));
    }
    finally { setMutating(false); }
  };

  const addShipmentBulk = async (ids) => {
    setNotice(null); setError(null);
    try {
      const r = await apiPost(`/api/logistics/load-plans/${id}/shipments/bulk`, { shipment_ids: ids });
      const added = r.added?.length || 0;
      const skipped = r.skipped || [];
      if (added > 0 && skipped.length === 0) {
        setNotice(`${added} spedizione${added > 1 ? "i" : ""} aggiunt${added > 1 ? "e" : "a"}`);
      } else if (added > 0 && skipped.length > 0) {
        setNotice(`${added} aggiunt${added > 1 ? "e" : "a"}, ${skipped.length} esclus${skipped.length > 1 ? "e" : "a"}: ${skipped.map((s) => s.code).join(", ")}`);
      } else if (skipped.length > 0) {
        setError(`Nessuna spedizione aggiunta. Motivi: ${skipped.map((s) => s.code).join(", ")}`);
      }
      await reload();
      setPicker(false);
    } catch (e) {
      setError(e?.data?.detail?.code || e?.message || String(e));
    }
  };

  const removeShipment = (sid) => doAction(
    () => apiDelete(`/api/logistics/load-plans/${id}/shipments/${sid}`),
    "Spedizione rimossa"
  );

  const moveShipment = async (targetPlanId) => {
    if (!moveOf) return;
    setNotice(null); setError(null);
    try {
      await apiPost(`/api/logistics/load-plans/${id}/shipments/${moveOf.shipment_id}/move`, {
        target_plan_id: targetPlanId,
      });
      setNotice(`Spedizione ${moveOf.shipment_number} spostata`);
      setMoveOf(null);
      await reload();
    } catch (e) {
      setError(e?.data?.detail?.code || e?.message || String(e));
    }
  };

  const sendToCarrier = () => doAction(
    () => apiPost(`/api/logistics/load-plans/${id}/send-to-carrier`, {}),
    plan.revision_number === 0 ? "Preavviso inviato al vettore" : "Revisione inviata al vettore"
  );

  const cancel = () => {
    if (!window.confirm("Confermi l'annullamento del Piano Carichi?")) return;
    doAction(() => apiPost(`/api/logistics/load-plans/${id}/cancel`, { reason: "Annullato da UI" }),
             "Piano annullato");
  };

  const confirmPlan = async () => {
    if (mutating) return;
    setNotice(null); setError(null); setConfirmErrors(null);
    setMutating(true);
    try {
      const r = await apiPost(`/api/logistics/load-plans/${id}/confirm`, { require_latest_revision_sent: true });
      setNotice(`Piano confermato: ${r.shipments_confirmed} spedizioni, ${r.orders_touched} ordini`);
      setConfirming(false);
      await reload();
    } catch (e) {
      if (e?.data?.detail?.code === "shipments_incomplete") {
        setConfirmErrors(e.data.detail.details || []);
      } else {
        setError(e?.data?.detail?.code || e?.message || String(e));
      }
    } finally { setMutating(false); }
  };

  const downloadXlsx = async () => {
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/logistics/load-plans/${id}/export.xlsx`;
    const r = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
    if (!r.ok) { setError("Export XLSX fallito"); return; }
    const blob = await r.blob();
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = `piano_carichi_${plan.loading_date}_${plan.revision_number}.xlsx`;
    link.click();
  };

  const openPrint = () => {
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/logistics/load-plans/${id}/print.html`;
    // Use fetch to attach auth header then open blob (backend enforces bearer)
    fetch(url, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.text())
      .then((html) => {
        const w = window.open("", "_blank");
        w.document.write(html);
        w.document.close();
      });
  };

  const totals = plan.totals || { deliveries: 0, pallets: 0, kg: 0 };
  const shipments = plan.shipments || [];

  return (
    <Section data-testid="loadplan-detail-page">
      {/* Responsive: nasconde colonne secondarie della tabella spedizioni sotto 1200px */}
      <style>{`
        @media (max-width: 1200px) {
          [data-testid="lp-shipments-table"] .lp-col-secondary { display: none; }
        }
      `}</style>

      <PageHeader
        breadcrumb={[
          { label: "Logistica", to: "/logistics" },
          { label: "Piano Carichi", to: "/logistics/load-plans" },
          { label: `LP · ${fmtDate(plan.loading_date)}` },
        ]}
        title={`Piano Carichi — ${fmtDate(plan.loading_date)}`}
        subtitle={
          <span style={{ display: "inline-flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
            <span>{carrier?.business_name || "—"}</span>
            <span>· Rev. {plan.revision_number}</span>
            <span>{statusBadge(plan)}</span>
          </span>
        }
        actions={
          <>
            {isMutable && (
              <Button onClick={sendToCarrier}
                      loading={mutating} disabled={mutating}
                      data-testid="lp-btn-send"
                      variant={plan.has_unsent_changes ? "accent" : "outline"}
                      title={plan.has_unsent_changes
                        ? "Ci sono modifiche non inviate al vettore"
                        : "Invia il preavviso o la revisione al vettore"}>
                {plan.revision_number === 0 ? "Prenota il carico" : "Invia revisione"}
              </Button>
            )}
            {canConfirm && (
              <Button onClick={() => setConfirming(true)} variant="primary" size="lg"
                      data-testid="lp-btn-confirm"
                      style={{ fontWeight: 700, boxShadow: "0 4px 12px rgba(15,76,129,0.35)" }}
                      title="Conferma definitivamente il Piano Carichi">
                <span aria-hidden="true" style={{ marginRight: 6 }}>✓</span>Conferma Piano
              </Button>
            )}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="ds-btn ds-btn--outline ds-btn--md"
                        data-testid="lp-btn-more"
                        style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                  <MoreHorizontal size={16} /> Altre azioni
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onSelect={downloadXlsx} data-testid="lp-btn-xlsx">
                  Export XLSX
                </DropdownMenuItem>
                <DropdownMenuItem onSelect={openPrint} data-testid="lp-btn-print">
                  Stampa
                </DropdownMenuItem>
                {isMutable && <DropdownMenuSeparator />}
                {isMutable && (
                  <DropdownMenuItem onSelect={cancel} data-testid="lp-btn-cancel"
                                    style={{ color: "#b91c1c" }}>
                    Annulla piano
                  </DropdownMenuItem>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          </>
        }
      />

      {notice && <Alert variant="success">{notice}</Alert>}
      {error && <Alert variant="danger">Errore: {error}</Alert>}

      {/* KPI totali — indicatore unico su has_unsent_changes: sta sul badge del bottone Invia */}
      <Card>
        <Card.Body>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: 16 }}>
            <div><Label>Consegne</Label><div style={{ fontSize: "1.6rem" }} data-testid="lp-total-deliveries">{totals.deliveries}</div></div>
            <div><Label>Pallet {totals.pallets_source === "estimated" && <span title="Stimato">*</span>}</Label><div style={{ fontSize: "1.6rem" }} data-testid="lp-total-pallets">{totals.pallets}</div></div>
            <div><Label>Kg</Label><div style={{ fontSize: "1.6rem" }} data-testid="lp-total-kg">{totals.kg}</div></div>
            <div><Label>Clienti</Label><div style={{ fontSize: "1.6rem" }}>{totals.customers}</div></div>
          </div>
        </Card.Body>
      </Card>

      {plan.applied_tariff && (
        <Card data-testid="lp-carrier-snapshot">
          <Card.Header>
            <button onClick={() => setCarrierOpen(v => !v)}
                    data-testid="lp-carrier-toggle"
                    aria-expanded={carrierOpen}
                    style={{ width: "100%", display: "flex", justifyContent: "space-between",
                             alignItems: "center", background: "transparent", border: "none",
                             cursor: "pointer", padding: 0, font: "inherit", textAlign: "left" }}>
              <span>
                <strong>Vettore assegnato:</strong>{" "}
                {plan.applied_tariff.carrier_name || carrier?.business_name || "—"}
              </span>
              {carrierOpen ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
            </button>
          </Card.Header>
          {carrierOpen && (
            <Card.Body>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 12 }}>
                <div>
                  <Label>Vettore</Label>
                  <div style={{ fontWeight: 600 }} data-testid="lp-carrier-name">
                    {plan.applied_tariff.carrier_name || carrier?.business_name || "—"}
                  </div>
                </div>
                {plan.applied_tariff.suggestion_shown && (
                  <div>
                    <Label>Suggerimento originale</Label>
                    <div>{plan.applied_tariff.suggestion_shown}</div>
                  </div>
                )}
                {plan.applied_tariff.tariff?.total_cost != null && (
                  <div>
                    <Label>Costo totale (snapshot)</Label>
                    <div>€ {Number(plan.applied_tariff.tariff.total_cost).toFixed(2).replace(".", ",")}</div>
                  </div>
                )}
                {plan.applied_tariff.tariff?.cost_per_pallet != null && (
                  <div>
                    <Label>€ / pallet (snapshot)</Label>
                    <div>€ {Number(plan.applied_tariff.tariff.cost_per_pallet).toFixed(2).replace(".", ",")}</div>
                  </div>
                )}
                {plan.applied_tariff.tariff?.transit_days != null && (
                  <div>
                    <Label>Giorni di transito (snapshot)</Label>
                    <div>{plan.applied_tariff.tariff.transit_days}</div>
                  </div>
                )}
              </div>
              <p className="muted" style={{ marginTop: 8, fontSize: ".85rem" }}>
                Il vettore è stato scelto nella fase di creazione/assegnazione del Piano Carichi. Per cambiarlo usa "Cambia Piano Carichi" sull'ordine.
              </p>
            </Card.Body>
          )}
        </Card>
      )}

      <Card>
        <Card.Header>
          <div style={{ display: "flex", justifyContent: "space-between",
                        alignItems: "center", gap: 8, flexWrap: "wrap" }}>
            <strong>Spedizioni ({shipments.length})</strong>
            {isMutable && (
              <Button variant="primary" size="sm" onClick={() => setPicker(true)}
                      data-testid="lp-open-picker-btn">+ Aggiungi spedizioni</Button>
            )}
          </div>
        </Card.Header>        <Card.Body>
          {shipments.length === 0 && <Alert tone="info">Nessuna spedizione in questo piano.</Alert>}
          {shipments.length > 0 && (
            <Table data-testid="lp-shipments-table">
              <thead><tr>
                <th>Ordine</th><th>Spedizione</th><th>Stato</th><th>Cliente</th>
                <th className="lp-col-secondary">Sede</th><th>Città</th><th className="lp-col-secondary">Prov</th>
                <th>Pallet</th><th>Kg</th>
                <th>Consegna prev.</th><th className="lp-col-secondary">Consegnato il</th>
                <th>DDT</th><th></th>
              </tr></thead>
              <tbody>
                {shipments.map((s) => {
                  const dp = s.delivery_point || {};
                  const st = s.status || "DRAFT";
                  return (
                    <tr key={s.shipment_id} data-testid={`lp-ship-${s.shipment_number}`}>
                      <td>{s.order_number}</td>
                      <td>{s.shipment_number}</td>
                      <td><Badge tone={st === "DELIVERED" ? "success" : st === "IN_TRANSIT" ? "info" : "neutral"}
                                 data-testid={`lp-ship-status-${s.shipment_number}`}>{st}</Badge></td>
                      <td>{s.customer_name}</td>
                      <td className="lp-col-secondary">{dp.name || "—"}</td>
                      <td>{dp.city || "—"}</td>
                      <td className="lp-col-secondary">{dp.province || "—"}</td>
                      <td>{s.pallets ?? "—"}{s.pallets_source === "estimated" && "*"}</td>
                      <td>{s.kg ?? "—"}</td>
                      <td>{s.expected_delivery_date || "—"}</td>
                      <td className="lp-col-secondary">{s.delivered_at ? new Date(s.delivered_at).toLocaleDateString("it-IT") : "—"}</td>
                      <td>{s.has_ddt ? <Badge tone="success">{s.ddt_number || "sì"}</Badge> : <Badge tone="warning">—</Badge>}</td>
                      <td>
                        <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                          {st === "DRAFT" && (
                            <Button size="sm" variant="primary"
                                    onClick={() => setConsuntivaOf(s)}
                                    data-testid={`lp-consuntiva-${s.shipment_number}`}>Consuntiva</Button>
                          )}
                          {st === "IN_TRANSIT" && (
                            <Button size="sm" variant="success"
                                    onClick={() => setDeliveredOf(s)}
                                    data-testid={`lp-mark-delivered-${s.shipment_number}`}>Segna consegnata</Button>
                          )}
                          {isMutable && st === "DRAFT" && (
                            <>
                              <Button size="sm" onClick={() => setMoveOf(s)}
                                      data-testid={`lp-move-${s.shipment_number}`}>Sposta</Button>
                              <Button size="sm" variant="danger" onClick={() => removeShipment(s.shipment_id)}
                                      loading={mutating} disabled={mutating}
                                      data-testid={`lp-remove-${s.shipment_number}`}>Rimuovi</Button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          )}
        </Card.Body>
      </Card>

      <Card>
        <Card.Header><strong>Storico revisioni ({snaps.length})</strong></Card.Header>
        <Card.Body>
          {snaps.length === 0 && <p className="muted">Nessuna revisione inviata.</p>}
          {snaps.length > 0 && (
            <Table>
              <thead><tr><th>Rev.</th><th>Tipo</th><th>Inviato</th><th>Destinatari</th><th>Consegne</th><th>Pallet</th><th>Kg</th></tr></thead>
              <tbody>
                {snaps.map((sn) => (
                  <tr key={sn.id}>
                    <td>{sn.revision_number}</td>
                    <td>{sn.kind}</td>
                    <td>{fmtDateTime(sn.sent_at)}</td>
                    <td style={{ fontSize: ".85rem" }}>{(sn.recipients || []).join(", ")}</td>
                    <td>{sn.payload?.totals?.deliveries || 0}</td>
                    <td>{sn.payload?.totals?.pallets || 0}</td>
                    <td>{sn.payload?.totals?.kg || 0}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Card.Body>
      </Card>

      {picker && (
        <ShipmentPickerModal
          planId={id}
          apiGet={apiGet}
          onClose={() => setPicker(false)}
          onAdd={addShipmentBulk}
        />
      )}
      {moveOf && (
        <MoveShipmentModal
          planId={id}
          shipment={moveOf}
          apiGet={apiGet}
          onClose={() => setMoveOf(null)}
          onMove={moveShipment}
        />
      )}

      {consuntivaOf && (
        <InlineConsuntivaModal
          shipment={consuntivaOf}
          planCarrierName={carrier?.business_name || ""}
          onClose={() => setConsuntivaOf(null)}
          onDone={(msg) => { setNotice(msg); setConsuntivaOf(null); reload(); }}
          onError={(e) => setError(e)}
        />
      )}

      {deliveredOf && (
        <InlineMarkDeliveredModal
          shipment={deliveredOf}
          onClose={() => setDeliveredOf(null)}
          onDone={(msg) => { setNotice(msg); setDeliveredOf(null); reload(); }}
          onError={(e) => setError(e)}
        />
      )}

      {confirming && (
        <div role="dialog" style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.45)",
                                    display: "flex", alignItems: "center", justifyContent: "center", zIndex: 50 }}>
          <div style={{ background: "#fff", padding: 24, borderRadius: 12, maxWidth: 640, width: "92%",
                        maxHeight: "90vh", overflow: "auto" }}>
            <h2>Conferma definitiva Piano Carichi</h2>
            <p>Stai per confermare definitivamente il Piano del <strong>{fmtDate(plan.loading_date)}</strong>
              con vettore <strong>{carrier?.business_name}</strong>.</p>
            <ul>
              <li>Spedizioni: <strong>{totals.deliveries}</strong></li>
              <li>Pallet: <strong>{totals.pallets}</strong></li>
              <li>Kg: <strong>{totals.kg}</strong></li>
              <li>Clienti: <strong>{totals.customers}</strong></li>
            </ul>
            <Alert variant="warning">Dopo la conferma non sarà possibile modificare o rimuovere spedizioni normalmente.
              Le Shipment passeranno in transito, gli ordini in consegna e il PFA maturerà.</Alert>
            {confirmErrors && (
              <div style={{ marginTop: 12 }}>
                <Alert variant="danger">Impossibile confermare — spedizioni incomplete:</Alert>
                <ul>
                  {confirmErrors.map((e, i) => (
                    <li key={i}>
                      <strong>{e.shipment_number}</strong>: {e.issues.join(", ")}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            <div style={{ display: "flex", gap: 12, marginTop: 20, justifyContent: "flex-end" }}>
              <Button onClick={() => { setConfirming(false); setConfirmErrors(null); }}>Annulla</Button>
              <Button variant="success" onClick={confirmPlan}
                      loading={mutating} disabled={mutating}
                      data-testid="lp-confirm-submit">Conferma definitivamente</Button>
            </div>
          </div>
        </div>
      )}
    </Section>
  );
}

// ---------- Shipment Picker Modal (Opzione A: table + multi-select) ----------

function ShipmentPickerModal({ planId, apiGet, onClose, onAdd }) {
  const [items, setItems] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [filters, setFilters] = useState({ q: "", region: "", status: "", date_from: "", date_to: "" });
  const [busy, setBusy] = useState(false);
  const [loadErr, setLoadErr] = useState(null);

  const load = async () => {
    setBusy(true); setLoadErr(null);
    try {
      const qp = new URLSearchParams();
      if (filters.q) qp.set("q", filters.q);
      if (filters.region) qp.set("region", filters.region);
      if (filters.status) qp.set("status", filters.status);
      if (filters.date_from) qp.set("date_from", filters.date_from);
      if (filters.date_to) qp.set("date_to", filters.date_to);
      const rows = await apiGet(`/api/logistics/load-plans/${planId}/available-shipments?${qp.toString()}`);
      setItems(rows || []);
    } catch (e) { setLoadErr(e?.message || String(e)); setItems([]); }
    finally { setBusy(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const toggle = (sid) => {
    const s = new Set(selected);
    if (s.has(sid)) s.delete(sid); else s.add(sid);
    setSelected(s);
  };
  const toggleAll = () => {
    if (selected.size === items.length) setSelected(new Set());
    else setSelected(new Set(items.map((r) => r.shipment_id)));
  };

  const regions = useMemo(() => {
    if (!items) return [];
    return Array.from(new Set(items.map((r) => r.customer_region).filter(Boolean))).sort();
  }, [items]);

  const filteredLocal = useMemo(() => {
    // Live client-side filter on top of server filters (immediate feedback)
    if (!items) return [];
    return items.filter((r) => {
      if (filters.region && r.customer_region !== filters.region) return false;
      return true;
    });
  }, [items, filters.region]);

  const submitSelected = async () => {
    if (selected.size === 0) return;
    setBusy(true);
    try {
      await onAdd(Array.from(selected));
    } finally { setBusy(false); }
  };

  return (
    <div role="dialog" data-testid="lp-picker-modal"
         style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.45)",
                  display: "flex", alignItems: "center", justifyContent: "center", zIndex: 60 }}>
      <div style={{ background: "#fff", borderRadius: 12, width: "95%", maxWidth: 1200,
                    maxHeight: "92vh", display: "flex", flexDirection: "column" }}>
        <div style={{ padding: "16px 20px", borderBottom: "1px solid #eee",
                      display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <h2 style={{ margin: 0 }}>Aggiungi spedizioni al Piano</h2>
          <Button onClick={onClose} data-testid="lp-picker-close">✕</Button>
        </div>

        {/* Filters */}
        <div style={{ padding: 16, borderBottom: "1px solid #eee",
                      display: "flex", gap: 12, flexWrap: "wrap", alignItems: "flex-end" }}>
          <div style={{ flex: "2 1 260px" }}>
            <Label>Cerca (ordine, spedizione, cliente)</Label>
            <Input value={filters.q} data-testid="lp-picker-q"
                   onChange={(e) => setFilters({ ...filters, q: e.target.value })}
                   onKeyDown={(e) => e.key === "Enter" && load()} />
          </div>
          <div style={{ minWidth: 160 }}>
            <Label>Regione</Label>
            <select className="ds-input" value={filters.region} data-testid="lp-picker-region"
                    onChange={(e) => setFilters({ ...filters, region: e.target.value })}>
              <option value="">Tutte</option>
              {regions.map((r) => <option key={r} value={r}>{r}</option>)}
            </select>
          </div>
          <div style={{ minWidth: 160 }}>
            <Label>Stato spedizione</Label>
            <select className="ds-input" value={filters.status} data-testid="lp-picker-status"
                    onChange={(e) => setFilters({ ...filters, status: e.target.value })}>
              <option value="">Tutti</option>
              <option value="DRAFT">In preparazione</option>
              <option value="IN_TRANSIT">In consegna</option>
            </select>
          </div>
          <div><Label>Data prev. dal</Label>
            <Input type="date" value={filters.date_from} data-testid="lp-picker-datefrom"
                   onChange={(e) => setFilters({ ...filters, date_from: e.target.value })} />
          </div>
          <div><Label>al</Label>
            <Input type="date" value={filters.date_to} data-testid="lp-picker-dateto"
                   onChange={(e) => setFilters({ ...filters, date_to: e.target.value })} />
          </div>
          <Button onClick={load} data-testid="lp-picker-apply">Applica</Button>
        </div>

        {/* Table */}
        <div style={{ flex: 1, overflow: "auto", padding: 16 }}>
          {busy && !items && <p className="muted">Caricamento…</p>}
          {loadErr && <Alert variant="danger">Errore: {loadErr}</Alert>}
          {items && filteredLocal.length === 0 && !busy && (
            <Alert tone="info">Nessuna spedizione compatibile con i filtri correnti.</Alert>
          )}
          {items && filteredLocal.length > 0 && (
            <div className="ds-table-wrap">
              <table className="ds-table" data-testid="lp-picker-table">
                <thead>
                  <tr>
                    <th style={{ width: 32 }}>
                      <input type="checkbox" data-testid="lp-picker-selectall"
                             checked={selected.size > 0 && selected.size === filteredLocal.length}
                             onChange={toggleAll} />
                    </th>
                    <th>Ordine</th><th>Spedizione</th><th>Cliente</th>
                    <th>Sede</th><th>Città</th><th>Regione</th>
                    <th>Data prev.</th><th>Pallet</th><th>Kg</th>
                    <th>Stato</th><th>Stralcio</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredLocal.map((r) => (
                    <tr key={r.shipment_id} data-testid={`lp-picker-row-${r.shipment_number}`}
                        style={{ background: selected.has(r.shipment_id) ? "#f0f7ff" : undefined }}>
                      <td>
                        <input type="checkbox" data-testid={`lp-picker-check-${r.shipment_number}`}
                               checked={selected.has(r.shipment_id)}
                               onChange={() => toggle(r.shipment_id)} />
                      </td>
                      <td>{r.order_number}</td>
                      <td>{r.shipment_number}</td>
                      <td>{r.customer_name}</td>
                      <td>{r.delivery_point?.name || "—"}</td>
                      <td>{r.delivery_point?.city || "—"} {r.delivery_point?.province && `(${r.delivery_point.province})`}</td>
                      <td>{r.customer_region || "—"}</td>
                      <td>{r.expected_delivery_date || r.requested_delivery_date || "—"}</td>
                      <td>{r.actual_pallets ?? r.estimated_pallets ?? "—"}</td>
                      <td>{r.shipped_weight_kg ?? "—"}</td>
                      <td>{r.shipment_status}</td>
                      <td>{r.is_partial_fulfillment ? <Badge tone="warning">Sì</Badge> : "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div style={{ padding: 16, borderTop: "1px solid #eee",
                      display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div className="muted" data-testid="lp-picker-count">
            {selected.size} selezionat{selected.size === 1 ? "a" : "e"} · {filteredLocal.length} risultat{filteredLocal.length === 1 ? "o" : "i"}
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <Button onClick={onClose}>Annulla</Button>
            <Button variant="primary" onClick={submitSelected}
                    loading={busy} disabled={selected.size === 0 || busy}
                    data-testid="lp-picker-submit">
              Aggiungi selezionate ({selected.size})
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ---------- Move Shipment Modal ----------

function MoveShipmentModal({ planId, shipment, apiGet, onClose, onMove }) {
  const [targets, setTargets] = useState(null);
  const [chosen, setChosen] = useState("");
  const [loadErr, setLoadErr] = useState(null);
  const [moving, setMoving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const rows = await apiGet(`/api/logistics/load-plans/${planId}/shipments/${shipment.shipment_id}/movable-targets`);
        setTargets(rows || []);
      } catch (e) { setLoadErr(e?.message || String(e)); setTargets([]); }
    })();
  }, [planId, shipment.shipment_id]);

  return (
    <div role="dialog" data-testid="lp-move-modal"
         style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.45)",
                  display: "flex", alignItems: "center", justifyContent: "center", zIndex: 60 }}>
      <div style={{ background: "#fff", borderRadius: 12, width: "95%", maxWidth: 720,
                    maxHeight: "90vh", overflow: "auto", padding: 20 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <h2 style={{ margin: 0 }}>Sposta spedizione</h2>
          <Button onClick={onClose} data-testid="lp-move-close">✕</Button>
        </div>
        <p style={{ marginTop: 8 }}>
          Sposta <strong>{shipment.shipment_number}</strong> ({shipment.customer_name}) verso un altro Piano Carichi compatibile.
        </p>
        {loadErr && <Alert variant="danger">Errore: {loadErr}</Alert>}
        {!targets && <p className="muted">Caricamento piani compatibili…</p>}
        {targets && targets.length === 0 && (
          <Alert tone="info">Nessun altro Piano Carichi compatibile disponibile. Verifica vettore, data di carico e stato dei piani.</Alert>
        )}
        {targets && targets.length > 0 && (
          <div className="ds-table-wrap" style={{ marginTop: 12 }}>
            <table className="ds-table">
              <thead>
                <tr>
                  <th style={{ width: 32 }}></th>
                  <th>Data carico</th><th>Vettore</th><th>Stato</th>
                  <th>Rev.</th><th>Pallet</th><th>Kg</th><th>Consegne</th>
                </tr>
              </thead>
              <tbody>
                {targets.map((t) => (
                  <tr key={t.id} data-testid={`lp-move-target-${t.id}`}
                      style={{ background: chosen === t.id ? "#f0f7ff" : undefined, cursor: "pointer" }}
                      onClick={() => setChosen(t.id)}>
                    <td><input type="radio" checked={chosen === t.id} onChange={() => setChosen(t.id)} /></td>
                    <td>{fmtDate(t.loading_date)}</td>
                    <td>{t.carrier_name}</td>
                    <td>{t.status}{t.has_unsent_changes && " *"}</td>
                    <td>{t.revision_number}</td>
                    <td>{t.totals?.pallets ?? "—"}</td>
                    <td>{t.totals?.kg ?? "—"}</td>
                    <td>{t.totals?.deliveries ?? "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 20 }}>
          <Button onClick={onClose}>Annulla</Button>
          <Button variant="primary"
                  onClick={async () => { if (moving) return; setMoving(true); try { await onMove(chosen); } finally { setMoving(false); } }}
                  loading={moving} disabled={!chosen || moving}
                  data-testid="lp-move-submit">
            Sposta
          </Button>
        </div>
      </div>
    </div>
  );
}



// ---------- Inline Consuntiva Modal (per single shipment inside Load Plan) ----------

function InlineConsuntivaModal({ shipment, planCarrierName, onClose, onDone, onError }) {
  const { apiGet, apiPost, token } = useAuth();
  const [prep, setPrep] = useState(null);
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);
  const [outcome, setOutcome] = useState("FULL"); // FULL | CLOSE_RESIDUAL | KEEP_RESIDUAL
  const [linesToShip, setLinesToShip] = useState({});
  const [expDate, setExpDate] = useState(shipment.expected_delivery_date || "");
  const [dwindow, setDwindow] = useState(shipment.delivery_window || "");
  const [pallets, setPallets] = useState(shipment.pallets != null ? String(shipment.pallets) : "");
  const [notes, setNotes] = useState("");
  const [ddtNumber, setDdtNumber] = useState(shipment.ddt_number || "");
  const [ddtDate, setDdtDate] = useState(shipment.ddt_date || "");
  const [ddtFile, setDdtFile] = useState(null);
  const [closeReason, setCloseReason] = useState("CUSTOMER_REQUEST");
  const [closeNote, setCloseNote] = useState("");
  // Campionatura omaggio (Feb 2026). Riusa DeliveryNote.kind='SAMPLE' esistente:
  // - Nessuna nuova colonna DB (has_sample deriva da DDT SAMPLE della Shipment)
  // - Nessuna modifica a OrderLine/ShipmentLine/pricing/PFA/residui
  // - Backend order_departure.py già allega SAMPLE al cliente + testo omaggio
  //   e load_plans.py:922 esclude SAMPLE dal pacchetto vettore.
  const [sampleEnabled, setSampleEnabled] = useState(false);
  const [sampleDdtFile, setSampleDdtFile] = useState(null);
  const [sampleDdtNumber, setSampleDdtNumber] = useState("");
  const [sampleDdtDate, setSampleDdtDate] = useState("");

  // DDT auto-lookup (Zucchetti import folder) — stesso pattern del wizard
  // consuntivo in LogisticsPages. Se il backend risponde 503, nascondiamo
  // il blocco Cerca DDT e resta il solo upload manuale.
  const [folderDisabled, setFolderDisabled] = useState(false);
  const [folderSearch, setFolderSearch] = useState(null);
  const [folderBusy, setFolderBusy] = useState(false);
  const [folderErr, setFolderErr] = useState(null);
  const [folderDdt, setFolderDdt] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const p = await apiGet(`/api/logistics/order/${shipment.order_id}/prepare`);
        setPrep(p);
        // Prefill quantities with full residuals for FULL outcome default
        const next = {};
        (p.lines || []).forEach(l => { next[l.order_line_id] = l.qty_residual; });
        setLinesToShip(next);
      } catch (e) {
        const d = e?.data?.detail || e?.response?.data?.detail;
        let msg = e?.message || String(e);
        if (typeof d === "object" && d) {
          if (d.code === "use_mark_departed_endpoint") {
            msg = "Per mettere la spedizione in consegna usa la funzione Conferma partenza.";
          } else if (d.code === "shipment_carrier_mismatch" || d.code === "carrier_mismatch") {
            msg = "Una o più spedizioni sono associate a un vettore diverso da quello del Piano Carichi. Correggi il vettore prima di confermare.";
          } else if (d.message) { msg = d.message; }
        } else if (typeof d === "string" && d === "use_mark_departed_endpoint") {
          msg = "Per mettere la spedizione in consegna usa la funzione Conferma partenza.";
        }
        setErr(msg);
      }
    })();
    // eslint-disable-next-line
  }, [shipment.order_id]);

  useEffect(() => {
    if (!prep) return;
    if (outcome === "FULL") {
      const next = {};
      (prep.lines || []).forEach(l => { next[l.order_line_id] = l.qty_residual; });
      setLinesToShip(next);
    }
  }, [outcome, prep]);

  const rows = (prep?.lines || []).map(l => {
    const shipped = Number(linesToShip[l.order_line_id] || 0);
    const residual = Math.max(0, l.qty_residual - shipped);
    return { ...l, ship_now: shipped, residual_after: residual };
  });
  // Feb 2026 — Fix radio parziali sempre disabled:
  // - `preHasResidual` = criterio PRE-consuntivo: c'è almeno una riga con
  //   qty_ordered - qty_already_shipped > 0 (da prep.lines). Serve SOLO per
  //   abilitare/disabilitare i radio "Evasa parzialmente".
  // - `postHasResidual` = criterio POST-consuntivo simulato (dopo le qty che
  //   l'operatore sta inserendo). Serve al submit per decidere se chiamare
  //   davvero create-residual-order / close-residual (evita chiamate con 0).
  const preHasResidual = (prep?.lines || []).some(l => Number(l.qty_residual) > 0);
  const hasResidual = rows.some(r => r.residual_after > 0);

  const submit = async () => {
    setBusy(true); setErr(null);
    try {
      const pv = parseInt(pallets, 10);
      if (!Number.isInteger(pv) || pv < 1) throw new Error("Pallet effettivi obbligatorio (intero ≥ 1)");
      // FULL: deriva SEMPRE da prep.lines per evitare race col prefill state.
      const lines = outcome === "FULL"
        ? (prep?.lines || [])
            .filter(l => Number(l.qty_residual) > 0)
            .map(l => ({ order_line_id: l.order_line_id, qty_cartons: Number(l.qty_residual) }))
        : Object.entries(linesToShip)
            .filter(([, q]) => Number(q) > 0)
            .map(([order_line_id, qty]) => ({ order_line_id, qty_cartons: Number(qty) }));
      if (lines.length === 0) throw new Error("Inserisci almeno una quantità da spedire");
      for (const r of rows) {
        if (r.ship_now > r.qty_residual) {
          throw new Error(`Riga ${r.sku}: qty (${r.ship_now}) > residuo (${r.qty_residual})`);
        }
      }
      if (outcome === "CLOSE_RESIDUAL" && closeReason === "OTHER" && !closeNote.trim()) {
        throw new Error("Motivo dello stralcio obbligatorio quando 'Altro' è selezionato");
      }

      // 1. Create/promote shipment as DRAFT (regola definitiva 2026-08-22:
      //    IN_TRANSIT si ottiene solo via /mark-departed che emette shipment.departed).
      const shp = await apiPost("/api/shipments", {
        order_id: shipment.order_id,
        carrier_id: null, // carrier already set by the Load Plan
        expected_delivery_date: expDate || null,
        delivery_window: dwindow || null,
        actual_pallets: pv,
        notes: notes || null,
        lines,
        status: "DRAFT",
      });

      // 2. DDT upload (skip: se già persistito via attach-from-folder)
      if (!folderDdt && ddtFile && ddtNumber && ddtDate) {
        const fd = new FormData();
        fd.append("ddt_number", ddtNumber);
        fd.append("ddt_date", ddtDate);
        fd.append("file", ddtFile);
        const API = process.env.REACT_APP_BACKEND_URL;
        const res = await fetch(`${API}/api/shipments/${shp.id}/ddt`, {
          method: "POST", body: fd, headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) throw new Error(`DDT upload failed: ${res.status}`);
      }

      // 2b. DDT SAMPLE (campionatura omaggio) — opzionale.
      // Se toggle ON e file caricato: upload con kind=SAMPLE. Backend
      // (order_departure.py) legge tutti i DDT non archiviati e appende
      // automaticamente sezione "Campionatura omaggio" alla mail cliente +
      // allega il PDF SAMPLE. Vettore non riceve SAMPLE (filtro esistente).
      if (sampleEnabled && sampleDdtFile) {
        if (!sampleDdtNumber.trim() || !sampleDdtDate) {
          throw new Error("Numero e data DDT campionatura sono obbligatori");
        }
        const fd = new FormData();
        fd.append("ddt_number", sampleDdtNumber);
        fd.append("ddt_date", sampleDdtDate);
        fd.append("kind", "SAMPLE");
        fd.append("file", sampleDdtFile);
        const API = process.env.REACT_APP_BACKEND_URL;
        const res = await fetch(`${API}/api/shipments/${shp.id}/ddt`, {
          method: "POST", body: fd, headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) throw new Error(`DDT campionatura upload failed: ${res.status}`);
      }

      // 2c. Conferma partenza — canonico: emette shipment.departed + email cliente.
      // Passa l'ETA anche nel payload come safety net: nel raro caso il DRAFT
      // esistente non sia stato aggiornato in [1] (es. race o rollback DB), il
      // service accetta l'ETA qui e non fallisce con missing_expected_delivery_date.
      await apiPost(`/api/shipments/${shp.id}/mark-departed`,
                    { expected_delivery_date: expDate || null });

      // 3. Flow V8 outcome
      if (outcome === "CLOSE_RESIDUAL" && hasResidual) {
        for (const r of rows) {
          if (r.residual_after > 0) {
            const body = { closed_qty_cartons: r.residual_after, close_reason: closeReason };
            if (closeReason === "OTHER") body.close_reason_note = closeNote;
            else if (closeNote) body.internal_note = closeNote;
            await apiPost(`/api/order-lines/${r.order_line_id}/close-residual`, body);
          }
        }
      } else if (outcome === "KEEP_RESIDUAL" && hasResidual) {
        await apiPost(`/api/shipments/${shp.id}/create-residual-order`, { idempotent: true });
      }

      onDone(`Spedizione ${shipment.shipment_number} consuntivata (${outcome})`);
    } catch (e) {
      // UX error mapping — nessun codice tecnico grezzo all'operatore.
      const raw = e?.data?.detail?.code || e?.data?.detail || e?.message || String(e);
      const code = typeof raw === "string" ? raw : "";
      const friendly = {
        no_residual_quantities_on_parent:
          "Non risultano quantità residue da creare per questo ordine.",
        use_mark_departed_endpoint:
          "Per mettere la spedizione in consegna usa la funzione Conferma partenza.",
        shipment_carrier_mismatch:
          "Il vettore della spedizione non coincide con quello del Piano Carichi. Correggi il vettore prima di procedere.",
        carrier_mismatch:
          "Il vettore della spedizione non coincide con quello del Piano Carichi. Correggi il vettore prima di procedere.",
      }[code];
      const msg = friendly || (typeof raw === "string" ? raw : JSON.stringify(raw));
      setErr(msg);
      onError && onError(msg);
    } finally { setBusy(false); }
  };

  return (
    <div role="dialog" data-testid="inline-consuntiva-modal"
         style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.45)",
                  display: "flex", alignItems: "center", justifyContent: "center", zIndex: 60 }}>
      <div style={{ background: "#fff", padding: 24, borderRadius: 12, maxWidth: 900, width: "94%",
                    maxHeight: "94vh", overflow: "auto" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 8 }}>
          <h2 style={{ margin: 0 }}>Consuntiva {shipment.shipment_number}</h2>
          <span className="muted">Vettore: <strong>{planCarrierName || "—"}</strong> · Ordine {shipment.order_number}</span>
        </div>
        {err && <Alert variant="danger" data-testid="inline-consuntiva-error">{err}</Alert>}
        {!prep && <p className="muted">Caricamento ordine…</p>}
        {prep && (
          <>
            <div style={{ display: "grid", gap: 10, marginBottom: 12 }}>
              {[
                { v: "FULL", t: "Evaso completamente" },
                { v: "CLOSE_RESIDUAL", t: "Evaso parzialmente — chiudi residuo (stralcio)" },
                { v: "KEEP_RESIDUAL", t: "Evaso parzialmente — crea ordine residuo (Flow V8)" },
              ].map(o => {
                // Feb 2026 — radio parziali abilitati se ESISTE residuo
                // PRE-consuntivo (almeno una riga con qty_residual > 0),
                // indipendentemente da cosa l'operatore ha già digitato.
                const disabled = (o.v !== "FULL") && !preHasResidual;
                return (
                <label key={o.v} data-testid={`inline-outcome-${o.v}`}
                       aria-disabled={disabled}
                       title={disabled
                         ? "Non ci sono quantità residue: usa 'Evaso completamente'"
                         : undefined}
                       style={{ display: "flex", gap: 10, alignItems: "center",
                                border: outcome === o.v ? "2px solid #059669" : "1px solid #e5e7eb",
                                borderRadius: 8, padding: 8,
                                cursor: disabled ? "not-allowed" : "pointer",
                                opacity: disabled ? 0.5 : 1,
                                background: outcome === o.v ? "#f0fdf4" : "#fff" }}>
                  <input type="radio" name="inline-outcome" value={o.v}
                         checked={outcome === o.v}
                         disabled={disabled}
                         onChange={() => setOutcome(o.v)} />
                  <strong>{o.t}</strong>
                  {disabled && (
                    <span className="muted" style={{ marginLeft: "auto", fontSize: ".78rem" }}
                          data-testid={`inline-outcome-disabled-${o.v}`}>
                      nessun residuo
                    </span>
                  )}
                </label>
              );})}
            </div>

            <div style={{ overflowX: "auto", marginBottom: 12 }}>
              <table className="ds-table">
                <thead><tr>
                  <th>SKU</th><th>Prodotto</th><th>Ord.</th><th>Già sped.</th><th>Residuo</th><th>Da spedire ora</th>
                  <th>{outcome === "CLOSE_RESIDUAL" ? "Stralciato" : outcome === "KEEP_RESIDUAL" ? "Va in nuovo ord." : ""}</th>
                </tr></thead>
                <tbody>
                  {rows.map(r => (
                    <tr key={r.order_line_id}>
                      <td>{r.sku}</td>
                      <td>{r.name}</td>
                      <td>{r.qty_ordered}</td>
                      <td>{r.qty_already_shipped}</td>
                      <td>{r.qty_residual}</td>
                      <td>
                        {outcome === "FULL" ? (
                          <span data-testid={`inline-ship-readonly-${r.sku}`}
                                style={{ display: "inline-block", padding: "4px 10px", borderRadius: 999,
                                         background: "#ecfdf5", color: "#065f46", fontWeight: 600 }}>
                            Spedisce tutto: {r.qty_residual}
                          </span>
                        ) : (
                          <Input type="number" min="0" max={r.qty_residual}
                                 data-testid={`inline-ship-${r.sku}`}
                                 value={linesToShip[r.order_line_id] ?? ""}
                                 onChange={e => setLinesToShip({ ...linesToShip, [r.order_line_id]: e.target.value })} />
                        )}
                      </td>
                      <td>{outcome === "FULL" ? "—" : (r.residual_after > 0
                            ? <span style={{ color: outcome === "CLOSE_RESIDUAL" ? "#b91c1c" : "#0369a1" }}
                                    data-testid={`inline-residual-preview-${r.sku}`}>{r.residual_after}</span>
                            : <span className="muted">—</span>)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {outcome === "FULL" && (
                <p className="muted" style={{ marginTop: 4, fontSize: ".85rem" }}
                   data-testid="inline-full-info">
                  Con &quot;Evaso completamente&quot; parte automaticamente l&apos;intero residuo di ogni riga. Per spedire meno usa uno dei due outcome parziali.
                </p>
              )}
            </div>

            {outcome === "CLOSE_RESIDUAL" && hasResidual && (
              <div style={{ display: "grid", gridTemplateColumns: "1fr 2fr", gap: 10, marginBottom: 12 }}>
                <div><Label>Motivo stralcio</Label>
                  <select className="ds-input" value={closeReason} data-testid="inline-close-reason"
                          onChange={e => setCloseReason(e.target.value)}>
                    <option value="CUSTOMER_REQUEST">Richiesta cliente</option>
                    <option value="SUBSTITUTION_REFUSED">Sostituzione non accettata</option>
                    <option value="NOT_NEEDED">Articolo non più necessario</option>
                    <option value="OTHER">Altro (nota obbligatoria)</option>
                  </select>
                </div>
                <div><Label>{closeReason === "OTHER" ? "Motivo *" : "Nota interna"}</Label>
                  <Input value={closeNote} data-testid="inline-close-note"
                         onChange={e => setCloseNote(e.target.value)} />
                </div>
              </div>
            )}

            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))", gap: 10, marginBottom: 12 }}>
              <div><Label>Data prevista consegna</Label>
                <Input type="date" value={expDate} data-testid="inline-expected-date"
                       onChange={e => setExpDate(e.target.value)} /></div>
              <div><Label>Fascia oraria</Label>
                <Input value={dwindow} placeholder="es. 09:00-13:00"
                       onChange={e => setDwindow(e.target.value)} /></div>
              <div><Label>Pallet effettivi *</Label>
                <Input type="number" min="1" value={pallets} data-testid="inline-pallets"
                       onChange={e => setPallets(e.target.value)} /></div>
              <div style={{ gridColumn: "1 / -1" }}><Label>Note</Label>
                <Input value={notes} onChange={e => setNotes(e.target.value)} /></div>
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))", gap: 10, marginBottom: 12,
                          border: "1px dashed #d1d5db", borderRadius: 8, padding: 10 }}>
              <div style={{ gridColumn: "1 / -1", fontWeight: 600 }} data-testid="inline-ddt-required-label">DDT *  (obbligatorio prima della partenza)</div>

              {/* Modalità 1 — Cerca DDT dalla cartella Zucchetti (opt-in via DDT_IMPORT_FOLDER) */}
              {!folderDisabled && !folderDdt && (
                <div style={{ gridColumn: "1 / -1", background: "#f8fafc", borderRadius: 6,
                              border: "1px solid #e2e8f0", padding: 10 }}
                     data-testid="inline-ddt-folder-lookup">
                  <div style={{ fontWeight: 600, marginBottom: 6 }}>Modalità 1 — Cerca DDT dalla cartella Zucchetti</div>
                  <div style={{ display: "flex", gap: 6, alignItems: "flex-end", flexWrap: "wrap" }}>
                    <div style={{ flex: "1 1 200px" }}>
                      <Label>Numero DDT (es. 2382/2026)</Label>
                      <Input value={ddtNumber} data-testid="inline-ddt-folder-input"
                             onChange={(e) => { setDdtNumber(e.target.value); setFolderSearch(null); setFolderErr(null); }} />
                    </div>
                    <Button size="sm" variant="primary" disabled={folderBusy || !ddtNumber.trim()}
                            data-testid="inline-ddt-folder-search-btn"
                            onClick={async () => {
                              setFolderBusy(true); setFolderErr(null); setFolderSearch(null);
                              try {
                                const q = encodeURIComponent(ddtNumber.trim());
                                const res = await apiGet(`/api/shipments/${shipment.shipment_id}/ddt/search?ddt_number=${q}`);
                                setFolderSearch(res);
                              } catch (e) {
                                if (e?.status === 503) setFolderDisabled(true);
                                else {
                                  // FastAPI pydantic 422 restituisce detail come array
                                  // di {loc,msg,type}: mostrare un messaggio human-readable.
                                  const d = e?.data?.detail;
                                  const msg = Array.isArray(d)
                                    ? "Numero DDT non valido oppure ricerca non eseguibile."
                                    : (typeof d === "string" ? d : (e?.message || String(e)));
                                  setFolderErr(msg);
                                }
                              } finally { setFolderBusy(false); }
                            }}>
                      {folderBusy ? "Cerco…" : "Cerca DDT"}
                    </Button>
                  </div>
                  {folderErr && <Alert tone="danger" data-testid="inline-ddt-folder-err" style={{ marginTop: 8 }}>{folderErr}</Alert>}
                  {folderSearch?.status === "not_found" && (
                    <Alert tone="info" data-testid="inline-ddt-folder-not-found" style={{ marginTop: 8 }}>
                      Nessun file trovato nella cartella per <strong>{folderSearch.normalized}</strong>. Usa la Modalità 2 (upload manuale).
                    </Alert>
                  )}
                  {folderSearch?.status === "found" && folderSearch.matches?.[0] && (
                    <Alert tone="success" data-testid="inline-ddt-folder-found" style={{ marginTop: 8 }}>
                      DDT trovato: <strong>{folderSearch.matches[0].filename}</strong>
                      <div style={{ marginTop: 8 }}>
                        <Button size="sm" variant="primary" disabled={folderBusy}
                                data-testid="inline-ddt-folder-attach-btn"
                                onClick={async () => {
                                  setFolderBusy(true); setFolderErr(null);
                                  try {
                                    const res = await apiPost(`/api/shipments/${shipment.shipment_id}/ddt/attach-from-folder`, {
                                      filename: folderSearch.matches[0].filename,
                                      ddt_number: ddtNumber.trim() || null,
                                      ddt_date: ddtDate || null,
                                    });
                                    setFolderDdt({ id: res.id, filename_original: res.filename_original });
                                  } catch (e) {
                                    setFolderErr(e?.data?.detail || e?.message || String(e));
                                  } finally { setFolderBusy(false); }
                                }}>
                          Allega DDT
                        </Button>
                      </div>
                    </Alert>
                  )}
                  {folderSearch?.status === "conflict" && (
                    <Alert tone="warning" data-testid="inline-ddt-folder-conflict" style={{ marginTop: 8 }}>
                      Trovati {folderSearch.matches.length} file compatibili — scegli manualmente:
                      <ul style={{ margin: "6px 0 0 18px" }}>
                        {folderSearch.matches.map(m => (
                          <li key={m.filename} style={{ marginTop: 4 }}>
                            <code>{m.filename}</code>{" "}
                            <Button size="sm" variant="outline" disabled={folderBusy}
                                    onClick={async () => {
                                      setFolderBusy(true); setFolderErr(null);
                                      try {
                                        const res = await apiPost(`/api/shipments/${shipment.shipment_id}/ddt/attach-from-folder`, {
                                          filename: m.filename, ddt_number: ddtNumber.trim() || null, ddt_date: ddtDate || null,
                                        });
                                        setFolderDdt({ id: res.id, filename_original: res.filename_original });
                                      } catch (e) {
                                        const d = e?.data?.detail;
                                        const msg = Array.isArray(d)
                                          ? "Numero DDT non valido oppure allega non eseguibile."
                                          : (typeof d === "string" ? d : (e?.message || String(e)));
                                        setFolderErr(msg);
                                      } finally { setFolderBusy(false); }
                                    }}>Allega</Button>
                          </li>
                        ))}
                      </ul>
                    </Alert>
                  )}
                </div>
              )}

              {folderDdt && (
                <div style={{ gridColumn: "1 / -1" }}>
                  <Alert tone="success" data-testid="inline-ddt-attached-from-folder">
                    <strong>DDT allegato dalla cartella:</strong> {folderDdt.filename_original}. Requisito soddisfatto — puoi consuntivare.
                  </Alert>
                </div>
              )}

              {/* Modalità 2 — Upload manuale (invariata, sempre disponibile come fallback) */}
              {!folderDdt && (
                <>
                  <div style={{ gridColumn: "1 / -1", fontWeight: 600, marginTop: 4 }}>Modalità 2 — Carica DDT manualmente</div>
                  <div><Label>Numero DDT</Label>
                    <Input value={ddtNumber} data-testid="inline-ddt-number"
                           onChange={e => setDdtNumber(e.target.value)} /></div>
                  <div><Label>Data DDT</Label>
                    <Input type="date" value={ddtDate} data-testid="inline-ddt-date"
                           onChange={e => setDdtDate(e.target.value)} /></div>
                  <div><Label>PDF DDT</Label>
                    <input type="file" accept="application/pdf" data-testid="inline-ddt-file"
                           onChange={e => setDdtFile(e.target.files?.[0] || null)} /></div>
                </>
              )}
            </div>

            {/* Campionatura omaggio (Feb 2026) — flag operativo + DDT SAMPLE + avviso cliente.
                Zero impatto su OrderLine/ShipmentLine/pricing/PFA/residui/pallet/peso.
                Backend order_departure.py deriva has_sample dai DeliveryNote kind=SAMPLE. */}
            <div style={{ marginBottom: 12, border: "1px solid #e5e7eb",
                          borderRadius: 8, padding: 10, background: "#fafafa" }}
                 data-testid="inline-sample-block">
              <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer" }}
                     data-testid="inline-sample-toggle-label">
                <input type="checkbox" checked={sampleEnabled}
                       data-testid="inline-sample-toggle"
                       onChange={e => setSampleEnabled(e.target.checked)} />
                <strong>Campionatura presente</strong>
                <span className="muted" style={{ fontSize: ".8rem" }}>
                  (omaggio — DDT dedicato inviato solo al cliente)
                </span>
              </label>
              {sampleEnabled && (
                <div style={{ marginTop: 10, display: "grid",
                              gridTemplateColumns: "repeat(auto-fill, minmax(180px, 1fr))",
                              gap: 10 }}
                     data-testid="inline-sample-fields">
                  <div style={{ gridColumn: "1 / -1", fontSize: ".85rem", color: "#475569" }}>
                    Carica il DDT della campionatura (PDF). Verrà salvato come
                    <strong> kind=SAMPLE</strong>, allegato alla mail cliente e
                    <strong> escluso dal pacchetto vettore</strong>.
                  </div>
                  <div><Label>Numero DDT campionatura</Label>
                    <Input value={sampleDdtNumber}
                           data-testid="inline-sample-ddt-number"
                           onChange={e => setSampleDdtNumber(e.target.value)} /></div>
                  <div><Label>Data DDT campionatura</Label>
                    <Input type="date" value={sampleDdtDate}
                           data-testid="inline-sample-ddt-date"
                           onChange={e => setSampleDdtDate(e.target.value)} /></div>
                  <div><Label>PDF DDT campionatura</Label>
                    <input type="file" accept="application/pdf"
                           data-testid="inline-sample-ddt-file"
                           onChange={e => setSampleDdtFile(e.target.files?.[0] || null)} /></div>
                </div>
              )}
            </div>

            <div style={{ display: "flex", gap: 10, justifyContent: "space-between", alignItems: "center" }}>
              <Button onClick={onClose} loading={busy} disabled={busy} data-testid="inline-consuntiva-back">
                ← Torna al Piano Carichi
              </Button>
              <Button variant="primary" onClick={submit} loading={busy} disabled={busy}
                      data-testid="inline-consuntiva-submit">
                {busy ? "Confermo…" : (
                  outcome === "FULL" ? "Consuntiva (completo)"
                  : outcome === "CLOSE_RESIDUAL" ? "Consuntiva + Chiudi residuo"
                  : "Consuntiva + Crea ordine residuo"
                )}
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}


// ---------- Inline Mark Delivered Modal ----------

function InlineMarkDeliveredModal({ shipment, onClose, onDone, onError }) {
  const { apiPost, token } = useAuth();
  const [deliveredOn, setDeliveredOn] = useState(new Date().toISOString().slice(0, 10));
  const [ddtNumber, setDdtNumber] = useState(shipment.ddt_number || "");
  const [ddtDate, setDdtDate] = useState(shipment.ddt_date || "");
  const [ddtFile, setDdtFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  const submit = async () => {
    setBusy(true); setErr(null);
    try {
      // Upload DDT if provided and shipment doesn't have one yet (or user replacing).
      if (ddtFile && ddtNumber && ddtDate) {
        const fd = new FormData();
        fd.append("ddt_number", ddtNumber);
        fd.append("ddt_date", ddtDate);
        fd.append("file", ddtFile);
        const API = process.env.REACT_APP_BACKEND_URL;
        const res = await fetch(`${API}/api/shipments/${shipment.shipment_id}/ddt`, {
          method: "POST", body: fd, headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) throw new Error(`DDT upload failed: ${res.status}`);
      }
      await apiPost(`/api/shipments/${shipment.shipment_id}/mark-delivered`,
                    deliveredOn ? { delivered_on: deliveredOn } : {});
      onDone(`Spedizione ${shipment.shipment_number} segnata come consegnata`);
    } catch (e) {
      const msg = e?.data?.detail?.code || e?.data?.detail || e?.message || String(e);
      setErr(typeof msg === "string" ? msg : JSON.stringify(msg));
      onError && onError(msg);
    } finally { setBusy(false); }
  };

  return (
    <div role="dialog" data-testid="inline-mark-delivered-modal"
         style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.45)",
                  display: "flex", alignItems: "center", justifyContent: "center", zIndex: 60 }}>
      <div style={{ background: "#fff", padding: 24, borderRadius: 12, maxWidth: 560, width: "94%" }}>
        <h2 style={{ marginTop: 0 }}>Consegna {shipment.shipment_number}</h2>
        {err && <Alert variant="danger" data-testid="inline-delivered-error">{err}</Alert>}
        <div style={{ display: "grid", gap: 10, marginBottom: 12 }}>
          <div><Label>Data effettiva consegna</Label>
            <Input type="date" value={deliveredOn} data-testid="inline-delivered-on"
                   onChange={e => setDeliveredOn(e.target.value)} /></div>
          {!shipment.has_ddt && (
            <div style={{ border: "1px dashed #d1d5db", borderRadius: 8, padding: 10 }}>
              <div style={{ fontWeight: 600, marginBottom: 6 }}>DDT mancante — carica ora (opzionale)</div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
                <div><Label>Numero</Label>
                  <Input value={ddtNumber} data-testid="inline-mdd-ddt-number"
                         onChange={e => setDdtNumber(e.target.value)} /></div>
                <div><Label>Data</Label>
                  <Input type="date" value={ddtDate} data-testid="inline-mdd-ddt-date"
                         onChange={e => setDdtDate(e.target.value)} /></div>
              </div>
              <div style={{ marginTop: 6 }}><Label>PDF</Label>
                <input type="file" accept="application/pdf" data-testid="inline-mdd-ddt-file"
                       onChange={e => setDdtFile(e.target.files?.[0] || null)} /></div>
            </div>
          )}
        </div>
        <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
          <Button onClick={onClose} loading={busy} disabled={busy}>Annulla</Button>
          <Button variant="success" onClick={submit} loading={busy} disabled={busy}
                  data-testid="inline-delivered-submit">
            {busy ? "Segno…" : "Segna consegnata"}
          </Button>
        </div>
      </div>
    </div>
  );
}

